// Role permission philosophy:
// - workspace_reader: read everywhere, plus self-service MCP client lifecycle
//   (create/delete) so readers can provision a client and reach the
//   reader-available MCP tools. Tool-level gating restricts what that client
//   can then do. No other mutations.
// - workspace_admin: every permission.
// - free_tier_reader: the same permissions as workspace_reader. The tiers
//   differ in workspace creation, which is gated by requiring a paid parent
//   workspace rather than by a permission — a `workspace:create` permission
//   withheld from the free tier would stop free_tier_admin containing
//   workspace_reader and break the nesting `lesserWorkspaceRole` depends on.
// - free_tier_admin: workspace_admin minus the permissions gated behind a
//   paid plan. The free tier holds no data export permission at all, so
//   free_tier_admin no longer contains workspace_reader — see
//   `assertRoleGrantable`, which checks permission containment rather than
//   rank so a free tier admin cannot mint a client that outreaches it.
// - platform_admin / platform_access_manager: workspace and member management.
//   There is no dedicated invite resource; workspace_member:invite covers
//   creating, deleting, and updating invites.

import type { RoleKey } from '.';
import type { Permission } from './permissions';
import { PERMISSIONS, Permissions } from './permissions';

const ALL_PERMISSIONS: readonly Permission[] = PERMISSIONS;

const READER_PERMS: readonly Permission[] = [
  ...ALL_PERMISSIONS.filter((p) => p.endsWith(':read')),
  Permissions.mcp_client.create,
  Permissions.mcp_client.delete,
];

// Permissions a paid plan buys.
const PAID_ONLY_PERMS: readonly Permission[] = [
  Permissions.data_export.create,
  Permissions.data_export.delete,
  Permissions.data_export.read,
];

const withoutPaidOnly = (perms: readonly Permission[]): readonly Permission[] =>
  perms.filter((p) => !PAID_ONLY_PERMS.includes(p));

const FREE_TIER_ADMIN_PERMS = withoutPaidOnly(ALL_PERMISSIONS);
const FREE_TIER_READER_PERMS = withoutPaidOnly(READER_PERMS);

const PLATFORM_ADMIN_PERMS: readonly Permission[] = [
  Permissions.workspace.read,
  Permissions.workspace.update,
  Permissions.workspace_member.invite,
  Permissions.workspace_member.delete,
  Permissions.workspace_member.read,
  Permissions.workspace_member.update,
  Permissions.user.read,
  Permissions.user.invite,
  Permissions.user.create,
];
const PLATFORM_ACCESS_MANAGER_PERMS: readonly Permission[] = [
  Permissions.workspace_member.invite,
  Permissions.workspace_member.delete,
  Permissions.workspace_member.read,
  Permissions.workspace_member.update,
  Permissions.workspace.update,
  Permissions.workspace.read,
];

export const ROLE_PERMISSIONS: Readonly<
  Record<RoleKey, ReadonlySet<Permission>>
> = {
  workspace_reader: new Set(READER_PERMS),
  free_tier_reader: new Set(FREE_TIER_READER_PERMS),
  workspace_admin: new Set(ALL_PERMISSIONS),
  free_tier_admin: new Set(FREE_TIER_ADMIN_PERMS),
  platform_admin: new Set(PLATFORM_ADMIN_PERMS),
  platform_access_manager: new Set(PLATFORM_ACCESS_MANAGER_PERMS),
};
